import React, {useCallback} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User } from "lucide-react";
import {useIntl} from "react-intl";
import {Link} from "react-router-dom";

import s from "./style.module.css";
import {restoreStore} from "../../store/restore.store";
import {login} from "../../config/router/unauthorizedRouter";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";

const RestorePage: React.FC = () => {
  const { formatMessage } = useIntl();

  const formSchema = z.object({
    username: z.string().min(1, { message: formatMessage({ id: 'ENTER_USERNAME' }) }),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
    },
  });

  const restorePassword = restoreStore(state => state.restore);
  const error = restoreStore(state => state.error);
  const message = restoreStore(state => state.message);
  const loading = restoreStore(state => state.loading);

  const onSubmit = useCallback((values: FormValues) => {
    restorePassword(values.username);
  }, [restorePassword]);

  return (
    <Card className={s.RestorePage}>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={formatMessage({ id: 'USER_NAME' })}
                      startIcon={<User />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button variant="primary" type="submit" loading={loading} className="w-full">
              {formatMessage({ id: 'RESTORE' })}
            </Button>

            {message && (
              <Alert variant="info">
                {message}
              </Alert>
            )}

            {error && (
              <Alert variant="error">
                {error.message}
              </Alert>
            )}

            <div className="flex justify-end">
              <Button variant="link" type="button" className="p-0">
                <Link to={login}>{formatMessage({ id: 'SIGN_IN' })}</Link>
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default RestorePage;
